import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import api from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";

export function CheckoutSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState({ status: "polling", payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!session_id) return;
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      count += 1;
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (cancelled) return;
        setStatus(data); setAttempts(count);
        if (data.payment_status === "paid" || count >= 10) return;
      } catch {}
      if (!cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id]);

  const paid = status.payment_status === "paid";
  return (
    <>
      <PublicNav />
      <Section className="pt-24 pb-24 text-center max-w-xl" testId="checkout-success">
        <CheckCircle2 size={48} strokeWidth={1.5} className="mx-auto mb-4 text-[color:var(--dt6-success)]" />
        <div className="dt6-chip dt6-chip-primary mb-4">/ CHECKOUT</div>
        <h1 className="font-heading text-4xl md:text-5xl font-black">
          {paid ? "You're in." : "Confirming payment..."}
        </h1>
        <p className="mt-4 text-[color:var(--dt6-muted)]">
          {paid
            ? `Your ${status.tier?.toUpperCase() || ""} subscription is active. Access the client portal to submit your first order.`
            : `Attempt ${attempts} / 10 — Stripe is finalizing the transaction.`}
        </p>
        <div className="mt-6 font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]" data-testid="checkout-session-id">
          / SESSION {String(session_id || "").slice(0, 12).toUpperCase()}
        </div>
        <div className="mt-8 flex justify-center gap-3">
          <button className="dt6-btn dt6-btn-primary" onClick={() => nav("/client")} disabled={!paid} data-testid="checkout-goto-portal">
            Enter client portal
          </button>
          <Link to="/pricing" className="dt6-btn" data-testid="checkout-back-pricing">Back to pricing</Link>
        </div>
      </Section>
      <PublicFooter />
    </>
  );
}

export function CheckoutCancel() {
  return (
    <>
      <PublicNav />
      <Section className="pt-24 pb-24 text-center max-w-xl" testId="checkout-cancel">
        <XCircle size={48} strokeWidth={1.5} className="mx-auto mb-4 text-[color:var(--dt6-danger)]" />
        <div className="dt6-chip mb-4">/ CANCELLED</div>
        <h1 className="font-heading text-4xl md:text-5xl font-black">Checkout aborted.</h1>
        <p className="mt-4 text-[color:var(--dt6-muted)]">
          No charge was made. Come back when you're ready to deploy.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/pricing" className="dt6-btn dt6-btn-primary" data-testid="cancel-back-pricing">Back to pricing</Link>
          <Link to="/" className="dt6-btn" data-testid="cancel-home">Home</Link>
        </div>
      </Section>
      <PublicFooter />
    </>
  );
}
